import React, { useState, Fragment } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { VoiceAgentCall } from '../../types';
import { ChevronDown, ChevronUp, Clock, MessageSquare } from 'lucide-react';
interface CallHistoryPanelProps {
  calls: VoiceAgentCall[];
}
export function CallHistoryPanel({
  calls
}: CallHistoryPanelProps) {
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const getStatusBadge = (status: VoiceAgentCall['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="info" data-id="element-37">Ausstehend</Badge>;
      case 'in_progress':
        return <Badge variant="warning" data-id="element-38">Läuft</Badge>;
      case 'completed':
        return <Badge variant="success" data-id="element-39">Abgeschlossen</Badge>;
      case 'failed':
        return <Badge variant="error" data-id="element-40">Fehlgeschlagen</Badge>;
    }
  };
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} Min`;
  };
  if (calls.length === 0) {
    return <Card data-id="element-41">
        <div className="text-center py-12" data-id="element-42">
          <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-43" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-44">
            Noch keine Anrufe durchgeführt
          </h3>
          <p className="text-sm text-slate-500" data-id="element-45">
            Starten Sie Ihren ersten AI Voice Agent Anruf über die Kundenliste.
          </p>
        </div>
      </Card>;
  }
  return <Card data-id="element-46">
      <div className="overflow-x-auto" data-id="element-47">
        <table className="w-full text-sm text-left" data-id="element-48">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-49">
            <tr data-id="element-50">
              <th className="px-4 py-3 font-medium" data-id="element-51">Kunde</th>
              <th className="px-4 py-3 font-medium" data-id="element-52">Prompt</th>
              <th className="px-4 py-3 font-medium" data-id="element-53">Status</th>
              <th className="px-4 py-3 font-medium" data-id="element-54">Datum</th>
              <th className="px-4 py-3 font-medium" data-id="element-55">Dauer</th>
              <th className="px-4 py-3 font-medium text-right" data-id="element-56">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-57">
            {calls.map(call => <Fragment key={call.id} data-id="element-58">
                <tr className="hover:bg-slate-50 transition-colors" data-id="element-59">
                  <td className="px-4 py-3" data-id="element-60">
                    <div className="font-medium text-slate-900" data-id="element-61">
                      {call.clientName}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-62">
                      {call.clientPhone}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-63">
                    {call.promptName}
                  </td>
                  <td className="px-4 py-3" data-id="element-64">{getStatusBadge(call.status)}</td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-65">
                    <div className="flex items-center gap-1" data-id="element-66">
                      <Clock className="h-3 w-3" data-id="element-67" />
                      {call.createdAt}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-68">
                    {formatDuration(call.duration)}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-69">
                    {call.transcript && <Button variant="ghost" size="sm" onClick={() => setExpandedCallId(expandedCallId === call.id ? null : call.id)} rightIcon={expandedCallId === call.id ? <ChevronUp className="h-4 w-4" data-id="element-71" /> : <ChevronDown className="h-4 w-4" data-id="element-72" />} data-id="element-70">
                        {expandedCallId === call.id ? 'Weniger' : 'Details'}
                      </Button>}
                  </td>
                </tr>

                {/* Expanded Transcript */}
                {expandedCallId === call.id && call.transcript && <tr data-id="element-73">
                    <td colSpan={6} className="px-4 py-4 bg-slate-50" data-id="element-74">
                      <div className="space-y-3" data-id="element-75">
                        <div data-id="element-76">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2" data-id="element-77">
                            Gesprächsverlauf
                          </h4>
                          <div className="bg-white p-3 rounded-lg border border-slate-200 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-id="element-78">
                            {call.transcript}
                          </div>
                        </div>
                        {call.notes && <div data-id="element-79">
                            <h4 className="text-sm font-semibold text-slate-700 mb-2" data-id="element-80">
                              Notizen
                            </h4>
                            <div className="bg-white p-3 rounded-lg border border-slate-200 text-sm text-slate-700" data-id="element-81">
                              {call.notes}
                            </div>
                          </div>}
                      </div>
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>
      </div>
    </Card>;
}